import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../context/AuthContext.jsx';
import {
  Key, Plus, Trash2, Copy, CheckCircle, AlertCircle,
  Code, TrendingUp, Zap, Shield, Clock, ExternalLink, RefreshCw, ArrowLeft, User
} from 'lucide-react';
import styles from './ApiKeys.module.css';
import ConnectedApps from '../components/ConnectedApps.jsx';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function ApiKeys() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const [keyName, setKeyName] = useState('');
  const [keyDescription, setKeyDescription] = useState('');
  const [rateLimit, setRateLimit] = useState(100);

  useEffect(() => {
    loadApiKeys();
  }, []);

  async function loadApiKeys() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const response = await fetch(`${API_URL}/v1/api-keys`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys || []);
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  }

  async function createApiKey() {
    if (!keyName.trim()) { alert('Please enter a key name'); return; }

    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${API_URL}/v1/api-keys`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: keyName.trim(),
          description: keyDescription.trim(),
          rate_limit_per_hour: rateLimit
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNewKey(data);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'vennio_signup_complete' });
        setShowCreateModal(false);
        setKeyName(''); setKeyDescription(''); setRateLimit(100);
        loadApiKeys();
      } else {
        const error = await response.json();
        alert(`Failed to create API key: ${error.message}`);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key');
    } finally {
      setCreating(false);
    }
  }

  async function revokeApiKey(keyId, keyName) {
    if (!confirm(`Are you sure you want to revoke "${keyName}"? This cannot be undone.`)) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${API_URL}/v1/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Revoked via dashboard' })
      });

      if (response.ok) {
        loadApiKeys();
      } else {
        alert('Failed to revoke API key');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      alert('Failed to revoke API key');
    }
  }

  function copyToClipboard(text, id = null) {
    navigator.clipboard.writeText(text);
    if (id) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <span className={styles.spinner} aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarLeft}>
            <Link to="/dashboard" className={styles.backLink}>
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
            <h1 className={styles.pageTitle}>
              <Key size={32} className={styles.pageTitleIcon} aria-hidden="true" />
              API Keys
            </h1>
            <p className={styles.pageSubtitle}>
              Programmatic access for AI agents, integrations, and custom applications
            </p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
            <Plus size={20} aria-hidden="true" />
            Create API Key
          </button>
        </div>
      </div>

      {/* Banner */}
      <div className={styles.banner}>
        <div className={styles.bannerInner}>
          <h2 className={styles.bannerTitle}>Infrastructure for Time</h2>
          <p className={styles.bannerSubtitle}>
            Build AI-powered scheduling into any application. Use our API to enable intelligent time coordination for your users.
          </p>
          <div className={styles.bannerGrid}>
            <div className={styles.bannerCard}>
              <div className={styles.bannerCardTitleRow}>
                <Zap size={20} aria-hidden="true" />
                <span>AI-Native API</span>
              </div>
              <p className={styles.bannerCardBody}>Built for Claude, ChatGPT, and autonomous agents</p>
            </div>
            <div className={styles.bannerCard}>
              <div className={styles.bannerCardTitleRow}>
                <Shield size={20} aria-hidden="true" />
                <span>Enterprise Security</span>
              </div>
              <p className={styles.bannerCardBody}>Rate limiting, usage tracking, and audit logs</p>
            </div>
            <div className={styles.bannerCard}>
              <div className={styles.bannerCardTitleRow}>
                <TrendingUp size={20} aria-hidden="true" />
                <span>Real-Time Analytics</span>
              </div>
              <p className={styles.bannerCardBody}>Track every API call with detailed metrics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.main}>
        {/* Business ID */}
        {user && (
          <div className={styles.businessIdSection}>
            <div className={styles.businessIdRow}>
              <div className={styles.businessIdLeft}>
                <div className={styles.businessIdLabel}>
                  <User size={14} aria-hidden="true" />
                  Business ID
                </div>
                <code className={styles.businessIdValue}>{user.id}</code>
              </div>
              <button
                onClick={() => copyToClipboard(user.id, 'business-id')}
                className={styles.businessIdCopyBtn}
                title="Copy Business ID"
              >
                {copiedId === 'business-id' ? (
                  <><CheckCircle size={14} aria-hidden="true" /> Copied</>
                ) : (
                  <><Copy size={14} aria-hidden="true" /> Copy</>
                )}
              </button>
            </div>
            <p className={styles.businessIdHint}>
              Use this ID to identify your business when integrating with the Vennio API
            </p>
          </div>
        )}

        {/* New key success banner */}
        {newKey && (
          <div className={styles.newKeyBanner}>
            <div className={styles.newKeyRow}>
              <CheckCircle size={24} className={styles.newKeyIcon} aria-hidden="true" />
              <div className={styles.newKeyContent}>
                <h3 className={styles.newKeyTitle}>API Key Created Successfully!</h3>
                <p className={styles.newKeyWarning}>
                  ⚠️ <strong>Important:</strong> This is the only time you'll see this key. Copy it now and store it securely!
                </p>
                <div className={styles.newKeyBox}>
                  <div className={styles.newKeyBoxRow}>
                    <code className={styles.newKeyCode}>{newKey.api_key?.key}</code>
                    <button onClick={() => copyToClipboard(newKey.api_key?.key)} className={styles.newKeyCopyBtn}>
                      <Copy size={16} aria-hidden="true" />
                      Copy
                    </button>
                  </div>
                </div>
                <div className={styles.newKeyLinks}>
                  <a
                    href="https://docs.vennio.app/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.newKeyLink}
                  >
                    <Code size={16} aria-hidden="true" />
                    View API Documentation
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </div>
                <button onClick={() => setNewKey(null)} className={styles.newKeyDismiss}>
                  I've saved my key, dismiss this message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick start (empty state only) */}
        {apiKeys.length === 0 && !newKey && (
          <div className={styles.quickStart}>
            <h3 className={styles.quickStartTitle}>🚀 Get Started in 3 Steps</h3>
            <ol className={styles.quickStartList}>
              <li className={styles.quickStartItem}>
                <span className={styles.quickStartNum}>1</span>
                <div className={styles.quickStartStep}>
                  <strong className={styles.quickStartStepTitle}>Create an API Key</strong>
                  <p className={styles.quickStartStepBody}>Click "Create API Key" above to generate your first key</p>
                </div>
              </li>
              <li className={styles.quickStartItem}>
                <span className={styles.quickStartNum}>2</span>
                <div className={styles.quickStartStep}>
                  <strong className={styles.quickStartStepTitle}>Make Your First Request</strong>
                  <div className={styles.codeBox}>
                    <code>
                      curl {API_URL}/v1/venn-links \<br/>
                      &nbsp;&nbsp;-H "Authorization: Bearer YOUR_API_KEY"
                    </code>
                  </div>
                </div>
              </li>
              <li className={styles.quickStartItem}>
                <span className={styles.quickStartNum}>3</span>
                <div className={styles.quickStartStep}>
                  <strong className={styles.quickStartStepTitle}>Build Something Amazing</strong>
                  <p className={styles.quickStartStepBody}>Integrate AI-powered scheduling into your app</p>
                </div>
              </li>
            </ol>
          </div>
        )}

        {/* Keys list */}
        {apiKeys.length > 0 ? (
          <div className={styles.keysList}>
            {apiKeys.map((key) => (
              <div key={key.id} className={styles.keyCard}>
                <div className={styles.keyCardInner}>
                  <div className={styles.keyLeft}>
                    <div className={styles.keyTitleRow}>
                      <h3 className={styles.keyName}>{key.name}</h3>
                      <span className={`${styles.keyStatusBadge} ${key.active ? styles.keyStatusActive : styles.keyStatusRevoked}`}>
                        {key.active ? 'Active' : 'Revoked'}
                      </span>
                    </div>
                    {key.description && (
                      <p className={styles.keyDesc}>{key.description}</p>
                    )}
                    <div className={styles.keyMeta}>
                      <div className={styles.keyMetaItem}>
                        <div className={styles.keyMetaLabel}>Rate Limit</div>
                        <div className={styles.keyMetaValue}>
                          <Clock size={14} className={styles.keyMetaIcon} aria-hidden="true" />
                          {key.rate_limit_per_hour} req/hour
                        </div>
                      </div>
                      <div className={styles.keyMetaItem}>
                        <div className={styles.keyMetaLabel}>Created</div>
                        <div className={styles.keyMetaValue}>{formatDate(key.created_at)}</div>
                      </div>
                      <div className={styles.keyMetaItem}>
                        <div className={styles.keyMetaLabel}>Last Used</div>
                        <div className={styles.keyMetaValue}>
                          {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                        </div>
                      </div>
                      <div className={styles.keyMetaItem}>
                        <div className={styles.keyMetaLabel}>Key ID</div>
                        <div className={styles.keyIdRow}>
                          <code className={styles.keyIdCode}>{key.id.substring(0, 8)}...</code>
                          <button onClick={() => copyToClipboard(key.id, key.id)} className={styles.copyIdBtn}>
                            {copiedId === key.id ? (
                              <CheckCircle size={14} className={styles.copiedIcon} aria-hidden="true" />
                            ) : (
                              <Copy size={14} aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {key.active && (
                    <button
                      onClick={() => revokeApiKey(key.id, key.name)}
                      className={styles.revokeBtn}
                      title="Revoke this API key"
                    >
                      <Trash2 size={20} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          !newKey && (
            <div className={styles.emptyKeys}>
              <Key size={48} className={styles.emptyIcon} aria-hidden="true" />
              <h3 className={styles.emptyTitle}>No API Keys Yet</h3>
              <p className={styles.emptyBody}>Create your first API key to start building with Vennio</p>
              <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
                <Plus size={20} aria-hidden="true" />
                Create API Key
              </button>
            </div>
          )
        )}

        {/* Connected OAuth apps (remote MCP connectors) */}
        <ConnectedApps />

        {/* Docs grid */}
        <div className={styles.docsGrid}>
          <a
            href={`${API_URL}/.well-known/ai-agent`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.docCard}
          >
            <div className={styles.docCardTitleRow}>
              <Code size={24} className={styles.docCardIcon} aria-hidden="true" />
              <h3 className={styles.docCardTitle}>API Discovery</h3>
            </div>
            <p className={styles.docCardBody}>View machine-readable API capabilities for AI agents</p>
            <div className={styles.docCardLink}>
              View Endpoints
              <ExternalLink size={14} aria-hidden="true" />
            </div>
          </a>

          <a
            href={`${API_URL}/openapi.yaml`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.docCard}
          >
            <div className={styles.docCardTitleRow}>
              <Code size={24} className={styles.docCardIcon} aria-hidden="true" />
              <h3 className={styles.docCardTitle}>OpenAPI Spec</h3>
            </div>
            <p className={styles.docCardBody}>Full OpenAPI 3.0 specification with examples</p>
            <div className={styles.docCardLink}>
              Download Spec
              <ExternalLink size={14} aria-hidden="true" />
            </div>
          </a>

          <div className={`${styles.docCard} ${styles.docCardUseCases}`}>
            <div className={styles.docCardTitleRow}>
              <Zap size={24} className={styles.docCardIcon} aria-hidden="true" />
              <h3 className={styles.docCardTitle}>Use Cases</h3>
            </div>
            <ul className={styles.useCaseList}>
              <li>• AI agent scheduling</li>
              <li>• Zapier integrations</li>
              <li>• Custom applications</li>
              <li>• Workflow automation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Create API Key</h2>

            <div className={styles.formFields}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Key Name *</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="Production API Key"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>Description</label>
                <textarea
                  value={keyDescription}
                  onChange={(e) => setKeyDescription(e.target.value)}
                  placeholder="For integration with our Rails app"
                  rows={3}
                  className={styles.formTextarea}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>Rate Limit (requests per hour)</label>
                <input
                  type="number"
                  value={rateLimit}
                  onChange={(e) => setRateLimit(parseInt(e.target.value))}
                  min="1"
                  max="10000"
                  className={styles.formInput}
                />
                <p className={styles.fieldHint}>Recommended: 100 for testing, 1000+ for production</p>
              </div>
            </div>

            <div className={styles.formActions}>
              <button onClick={() => setShowCreateModal(false)} disabled={creating} className={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={createApiKey}
                disabled={creating || !keyName.trim()}
                className={styles.submitBtn}
              >
                {creating ? (
                  <>
                    <RefreshCw size={16} className={styles.spinningIcon} aria-hidden="true" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} aria-hidden="true" />
                    Create Key
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
